import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Txt from 'components/common/ui/Txt';

/******************************************************************
세그먼트 게이지 (SegmentProgress의 반원 버전)
 ******************************************************************/

// ----- 모양 값 (SVG viewBox 기준 숫자) -----
const CENTER_X = 120;
const CENTER_Y = 118;
const INNER_RADIUS = 78;
const OUTER_RADIUS = 114;
const GAP = 3;
const CORNER_RADIUS = 3;
const VIEW_BOX = '0 0 240 122';

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

// 각도·반지름 → SVG 좌표 (SVG는 y가 아래로 커져서 sin 앞에 -)
const toPoint = (angleDeg, radius) => {
	const x = CENTER_X + radius * Math.cos(toRad(angleDeg));
	const y = CENTER_Y - radius * Math.sin(toRad(angleDeg));
	return `${x.toFixed(2)},${y.toFixed(2)}`;
};

// i번째 칸의 꼭짓점 4개 (바깥 왼쪽 → 바깥 오른쪽 → 안쪽 오른쪽 → 안쪽 왼쪽)
// 반지름마다 "간격의 절반"만큼 각도를 깎아야 안쪽/바깥쪽 간격 폭이 같아짐 (asin)
const getSegmentPoints = (i, segments) => {
	const step = 180 / segments;
	const centerAngle = 180 - (i + 0.5) * step; // i 0 = 왼쪽 끝
	const innerRadius = INNER_RADIUS + CORNER_RADIUS;
	const outerRadius = OUTER_RADIUS - CORNER_RADIUS;
	const halfGap = GAP / 2 + CORNER_RADIUS;

	const edgeAngles = (radius) => {
		const cut = toDeg(Math.asin(halfGap / radius));
		return [centerAngle + step / 2 - cut, centerAngle - step / 2 + cut];
	};

	const [outerLeft, outerRight] = edgeAngles(outerRadius);
	const [innerLeft, innerRight] = edgeAngles(innerRadius);

	return [
		toPoint(outerLeft, outerRadius),
		toPoint(outerRight, outerRadius),
		toPoint(innerRight, innerRadius),
		toPoint(innerLeft, innerRadius),
	].join(' ');
};

const SegmentGauge = ({
	value = 0,
	segments = 20,
	ariaLabel = '',
	className = '',
	time = null,
}) => {
	// 0~100 범위를 벗어난 값이 와도 게이지가 깨지지 않게 고정
	const percent = Math.min(100, Math.max(0, value));
	// 72% → 20칸 중 14칸
	const filledCount = Math.round((percent / 100) * segments);

	// 칸 모양은 value와 상관없이 segments가 바뀔 때만 다시 계산
	const segmentPoints = useMemo(
		() => Array.from({ length: segments }, (_, i) => getSegmentPoints(i, segments)),
		[segments],
	);

	return (
		<div
			className={['segment_gauge', className].filter(Boolean).join(' ')}
			role='progressbar'
			aria-valuenow={percent}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-label={ariaLabel || undefined}
		>
			<svg viewBox={VIEW_BOX} aria-hidden='true' focusable='false'>
				{segmentPoints.map((points, i) => {
					const isFilled = i < filledCount;
					// 칸 위치에 따라 0%(진한 보라) → 100%(밝은 보라) 비율
					const mixRatio = segments > 1 ? Math.round((i / (segments - 1)) * 100) : 0;

					return (
						<polygon
							key={i}
							className={`segment_gauge_item${isFilled ? ' filled' : ''}`}
							style={isFilled ? { '--mix_ratio': `${mixRatio}%` } : undefined}
							points={points}
							strokeWidth={CORNER_RADIUS * 2}
							strokeLinejoin='round'
						/>
					);
				})}
			</svg>

			<div className='info_box'>
				<Txt variant='num_lg' color='black3'>{value}%</Txt>
				<Txt color='black3'>{time}</Txt>
			</div>
		</div>
	);
};

SegmentGauge.propTypes = {
	value: PropTypes.number,
	segments: PropTypes.number,
	ariaLabel: PropTypes.string,
	className: PropTypes.string,
	time: PropTypes.node
};

export default SegmentGauge;